import moment from 'moment'
import React, {useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {Link, Outlet, useLocation, useNavigate} from 'react-router-dom'
import BouncedTab from './EOFYSaleSubSections/BouncedTab'
import OptOutTab from './EOFYSaleSubSections/OptOutTab'
import ShortLink from './EOFYSaleSubSections/ShortLink'
import Snapshot from './EOFYSaleSubSections/Snapshot'
import {
  GetReportId,
  GetSingleMessageData,
  getSingleMessageReport,
} from './reportSlices/reportAnalytics'
import {KTSVG} from '../../../../_metronic/helpers'
import {
  useLazyGetStaggerMessagesQuery,
  usePauseCampaignMutation,
  useResumeCampaignMutation,
  useCancelCampaignMutation as useCancelSendingCampaignMutation,
} from '../../../../services/cellcast.v2'
import {useEdges} from 'reactflow'
import {toast} from 'react-toastify'
import {MessageSchedule} from '../../../../services/cellcast.v2.types'
import {red} from '@material-ui/core/colors'
import {useTimezone} from '../../../hooks/useTimezone'
import {getIsDefaultRegion} from '../accounts/Overview/OverviewSlice/UserDetailsSlice'
import {OverlayTrigger, Spinner, Tooltip} from 'react-bootstrap'
import DeleteConfirmationPopup from '../../../Components/DeleteConfirmationPopup'
import {useCancelCampaignMutation} from '../../../../services/cellcast'

type ActionButtonProps = {
  show: boolean
  tooltip: string
  icon: string
  colorClass: string
  loading: boolean
  loadingIdParam: string | undefined
  setIdParam: (id: string) => void
  openConfirmation: (open: boolean) => void
}

const ReportPageHeader = () => {
  const navigate = useNavigate()
  const param = useLocation()
  const dispatch = useDispatch()
  const messageData = useSelector(GetSingleMessageData)
  const ReportId = useSelector(GetReportId)
  const isDefaultRegion = useSelector(getIsDefaultRegion)

  // hooks
  const {toLocalTimezone} = useTimezone()
  const [fetchStaggerMessage, {data: staggerResponse}] = useLazyGetStaggerMessagesQuery()

  const [isStagger, setIsStagger] = useState(false)
  const [staggerMessage, setStaggerMessage] = useState<MessageSchedule[]>([])
  const [staggerMessageAmount, setStaggerMessageAmount] = useState<Number>(0)
  const [latestSend, setLastStaggerSend] = useState<MessageSchedule | null>(null)

  // cancel
  const [isOpenCancelConfirmation, setIsOpenCancelConfirmation] = useState(false)
  const [cancelIdParam, setCancelIdParam] = useState('')
  const [cancelSendingMessage, {isLoading: cancelCampaignLoading}] =
    useCancelSendingCampaignMutation()

  // pause
  const [isOpenPauseConfirmation, setIsOpenPauseConfirmation] = useState(false)
  const [pauseIdParam, setPauseIdParam] = useState('')
  const [pauseSendingMessage, {isLoading: pauseCampaignLoading}] = usePauseCampaignMutation()

  // resume
  const [isOpenResumeConfirmation, setIsOpenResumeConfirmation] = useState(false)
  const [resumeIdParam, setResumeIdParam] = useState('')
  const [resumeSendingMessage, {isLoading: resumeCampaignLoading}] = useResumeCampaignMutation()

  const [isOpenConfirmation, setIsOpenConfirmation] = useState(false)
  const [cancelParam, setCancelParam] = useState<{
    id: string
    staggerMessages: boolean
    staggerId: null | string
  } | null>(null)

  const [cancelCampaign, {isLoading: cancelLoading}] = useCancelCampaignMutation()


  const cancelConfirmation = useMemo(
    () => (
      <DeleteConfirmationPopup
        show={isOpenConfirmation}
        handleClose={() => {
          setIsOpenConfirmation(false)
          setCancelParam(null)
        }}
        Message={'Are you sure, you want to cancel the campaign ?'}
        confirm={() => {
          if (cancelParam) {
            handleCancelCampaign(cancelParam.id, cancelParam.staggerMessages, cancelParam.staggerId)
            setCancelParam(null)
          }
        }}
        confirmBtnTitle='Yes, cancel campaign'
      />
    ),
    [isOpenConfirmation, cancelParam]
  )

  const handleCancelCampaign = (
    id: string,
    staggerMessages: boolean = false,
    staggerId: null | string = null
  ) => {
    cancelCampaign({id, staggerId, staggerMessages})
      .then((res) => {
        if ('data' in res) {
          toast.success(res.data?.message)
        } else if ('error' in res) {
          toast.error(res.error)
          console.error('Error cancel campaign:', res.error)
        }
      })
      .finally(() => {
        window.location.reload()
      })
  }

  const cancelSendingMessageConfirmation = useMemo(
    () => (
      <DeleteConfirmationPopup
        show={isOpenCancelConfirmation}
        handleClose={() => {
          setIsOpenCancelConfirmation(false)
          setCancelIdParam('')
        }}
        Message={'Are you sure, you want to cancel this campaign ?'}
        confirm={() => {
          if (cancelIdParam) {
            cancelSendingMessage({
              bulkId: cancelIdParam,
              type: ReportId?.camp_type,
            })
              .then((res) => {
                if ('data' in res) {
                  toast.success(res.data?.message)
                } else if ('error' in res) {
                  toast.error(res.error)
                  console.error('Error cancel campaign :', res.error)
                }
              })
              .catch((err) => {
                toast.error(err.error)
              })
              .finally(() => {
                setIsOpenCancelConfirmation(false)
                setCancelIdParam('')
                window.location.reload()
              })
          }
        }}
        confirmBtnTitle='Yes, cancel campaign'
      />
    ),
    [isOpenCancelConfirmation, cancelIdParam, cancelSendingMessage]
  )

  const pauseSendingMessageConfirmation = useMemo(
    () => (
      <DeleteConfirmationPopup
        show={isOpenPauseConfirmation}
        handleClose={() => {
          setIsOpenPauseConfirmation(false)
          setPauseIdParam('')
        }}
        Message={'Are you sure, you want to pause this campaign ?'}
        confirm={() => {
          if (pauseIdParam) {
            pauseSendingMessage({
              bulkId: pauseIdParam,
              type: ReportId?.camp_type,
            })
              .then((res) => {
                if ('data' in res) {
                  toast.success(res.data?.message)
                } else if ('error' in res) {
                  toast.error(res.error)
                  console.error('Error pause campaign :', res.error)
                }
              })
              .catch((err) => {
                toast.error(err.error)
              })
              .finally(() => {
                setIsOpenPauseConfirmation(false)
                setPauseIdParam('')
                window.location.reload()
              })
          }
        }}
        confirmBtnTitle='Yes, pause campaign'
      />
    ),
    [isOpenPauseConfirmation, pauseIdParam, pauseSendingMessage]
  )

  const resumeSendingMessageConfirmation = useMemo(
    () => (
      <DeleteConfirmationPopup
        show={isOpenResumeConfirmation}
        handleClose={() => {
          setIsOpenResumeConfirmation(false)
          setResumeIdParam('')
        }}
        selectedBulk={messageData}
        withIntervalConfirmation={true}
        Message={'Do you want to resume this campaign, please select an option below?'}
        confirm={({interval, scheduleAt}: {interval: number; scheduleAt: string}) => {
          if (resumeIdParam) {
            resumeSendingMessage({
              bulkId: resumeIdParam,
              type: ReportId?.camp_type,
              interval,
              scheduleAt: scheduleAt ? moment(scheduleAt).unix() : 0,
            })
              .then((res) => {
                if ('data' in res) {
                  toast.success(res.data?.message)
                } else if ('error' in res) {
                  toast.error(res.error)
                  console.error('Error resume campaign :', res.error)
                }
              })
              .catch((err) => {
                toast.error(err.error)
              })
              .finally(() => {
                setIsOpenResumeConfirmation(false)
                setResumeIdParam('')
                window.location.reload()
              })
          }
        }}
        confirmBtnTitle='Yes, resume campaign'
      />
    ),
    [isOpenResumeConfirmation, resumeIdParam, resumeSendingMessage]
  )

  const renderActionButton = ({
    show,
    tooltip,
    icon,
    colorClass,
    loading,
    loadingIdParam,
    setIdParam,
    openConfirmation,
  }: ActionButtonProps) => {
    if (!show) return null

    const id = messageData?.bulkId || messageData?._id
    const isLoading = loading && [messageData?._id, messageData?.bulkId].includes(loadingIdParam)

    return (
      <OverlayTrigger placement='top' overlay={<Tooltip id='button-tooltip'>{tooltip}</Tooltip>}>
        <button
          className={`btn btn-disable btn-sm btn-icon btn-bg-light ${colorClass} ms-3`}
          onClick={() => {
            setIdParam(id)
            openConfirmation(true)
          }}
        >
          {isLoading ? <Spinner /> : <i className={`bi ${icon} fs-5`}></i>}
        </button>
      </OverlayTrigger>
    )
  }

  useEffect(() => {
    if (messageData && messageData.staggerId) {
      fetchStaggerMessage(messageData.staggerId)
        .then((res) => {
          if ('data' in res) {
            setIsStagger(true)
            if (res.data?.data && Array.isArray(res.data.data)) {
              let tmpCredit = 0

              const formattedData = res.data.data.map((el) => {
                if (el.deductibleCredits) {
                  tmpCredit += el.deductibleCredits
                }

                const tmpData = {...el}
                if (tmpData.scheduleAt) {
                  tmpData['scheduleAtDate'] = toLocalTimezone(new Date(tmpData.scheduleAt))
                }

                return tmpData
              })

              setStaggerMessage(formattedData)
              setStaggerMessageAmount(tmpCredit)

              // Step 1: Filter for messages with status 'complete'
              const completedMessages = res.data.data.filter((item) => item.status === 'complete')

              // Step 2: Sort by 'scheduleAt' in descending order
              completedMessages.sort(
                (a, b) => new Date(b.scheduleAt).getTime() - new Date(a.scheduleAt).getTime()
              )

              // Step 3: Get the last message
              const lastMessage = completedMessages.length > 0 ? completedMessages[0] : null
              setLastStaggerSend(lastMessage)
            }

            toast.success(res.data?.message)
          } else if ('error' in res) {
            toast.error(res.error)
            console.error('Error Fetch Message:', res.error)
          }
        })
        .catch((err) => {
          toast.error(err.error)
        })
    }
  }, [messageData])

  const renderRegionData = () => {
    const messageData = ReportId?.data

    return (
      <div className='d-flex flex-wrap flex-sm-nowrap m-3'>
        <div className='me-7 mb-4'>
          <div className='symbol symbol-sms symbol-100px symbol-lg-160px symbol-fixed position-relative'>
            <span>{messageData?.message_type?.toUpperCase()}</span>
          </div>
        </div>
        <div className='flex-grow-1'>
          <div className='d-flex justify-content-between align-items-start flex-wrap mb-2'>
            <div className='d-flex flex-column'>
              <div className='d-flex align-items-center mb-2'>
                <span className='text-gray-800 fs-1 fw-bolder me-1' data-testid='bmTitle'>
                  {messageData?.name}
                </span>
                {messageData?.canceled_at ? (
                  <span className={` m-3 badge badge-light-danger fw-bold`} data-testid='bmStatus'>
                    Cancelled by user
                  </span>
                ) : (
                  <span
                    className={` m-3 badge badge-light-${
                      messageData?.status === 'completed' ? 'success' : 'success'
                    } fw-bold`}
                    data-testid='bmStatus'
                  >
                    {messageData?.status === 'canceled' ? "CANCELLED" : messageData?.status?.toUpperCase() || 'Completed'}
                  </span>
                )}
              </div>
              <div className='d-flex flex-wrap fw-bold fs-6 mb-1 pe-2'>
                <span
                  className='d-flex align-items-center text-hover-primary me-5 mb-2'
                  style={{color: '#767687'}}
                  data-testid='bmSentOn'
                >
                  {!isStagger && (
                    <>
                      <span className='me-1' style={{color: '#191C30'}}>
                        Sent on
                      </span>
                      {moment(messageData?.created_at).format('HH:mm DD/MM/YYYY')}
                    </>
                  )}

                  {isStagger && (
                    <>
                      <span className='me-1' style={{color: '#191C30'}}>
                        Sent on
                      </span>
                      <br />
                      {staggerMessage
                        .map((el) => moment(el?.scheduleAtDate).format('HH:mm DD/MM/YYYY'))
                        .join(' , ')}
                    </>
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className='d-flex flex-wrap flex-stack account-stack'>
            <div className='d-flex flex-column flex-grow-1 pe-8'>
              <div className='d-flex flex-wrap'>
                <div className='border border-gray-300 border-solid rounded min-w-125px py-2 px-4 me-4 mb-3'>
                  <div className='d-flex align-items-center'>
                    <div className='fs-3 fw-bolder' data-testid='bmSendCost'>
                      ${+messageData?.const_amount?.toFixed(4)}
                    </div>
                  </div>
                  <div className='fw-bold fs-6 text-gray-400'>Send cost</div>
                </div>
                <div className='border border-gray-300 border-solid rounded min-w-125px py-2 px-4 me-4 mb-3'>
                  <div className='d-flex align-items-center'>
                    <div className='fs-3 fw-bolder' data-testid='bmDateSent'>
                      {isStagger
                        ? moment(latestSend?.scheduleAt).format('HH:mm DD/MM/YYYY')
                        : moment(messageData?.created_at).format('HH:mm DD/MM/YYYY')}
                    </div>
                  </div>
                  <div className='fw-bold fs-6 text-gray-400'>
                    {isStagger ? 'Last sent' : 'Date sent'}
                  </div>
                </div>
                <div className='border border-gray-300 border-solid rounded min-w-125px py-2 px-4 me-4 mb-3'>
                  <div className='d-flex align-items-center'>
                    <div className='fs-3 fw-bolder' data-testid='bmSenderID'>
                      {messageData?.sender}
                    </div>
                  </div>
                  <div className='fw-bold fs-6 text-gray-400'>Sender ID</div>
                </div>
                <div
                  className='border border-gray-300 border-solid rounded min-w-125px py-2 px-4 me-4 mb-3'
                  data-testid='bmSentTo'
                >
                  <div className='d-flex align-items-center'>
                    <div className='fs-3 fw-bolder'>
                      {messageData?.to?.type == 'segmentOrTags'
                        ? 'List, Segments & Tags'
                        : messageData?.to?.type}{' '}
                      ({messageData?.total_recipient})
                    </div>
                  </div>
                  <div className='fw-bold fs-6 text-gray-400'>
                    To{' '}
                    {messageData?.to?.type === 'segmentOrTags' &&
                      messageData?.targetNames &&
                      Array.isArray(messageData?.targetNames) &&
                      ' : ' + messageData?.targetNames.join(',')}
                  </div>
                </div>

                <div className='border border-gray-300 border-solid rounded min-w-125px py-2 px-4 me-4 mb-3'>
                  <div className='d-flex align-items-center'>
                    <div className='fs-3 fw-bolder' data-testid='bmCreditUsed'>
                      {+messageData?.credit_amount?.toFixed(4)}
                    </div>
                  </div>
                  <div className='fw-bold fs-6 text-gray-400'>Credit Used</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderDefaultRegion = () => {
    return (
      <div className='d-flex flex-wrap flex-sm-nowrap m-3'>
        <div className='me-7 mb-4'>
          <div className='symbol symbol-sms symbol-100px symbol-lg-160px symbol-fixed position-relative'>
            <span>{messageData?.camp_type?.toUpperCase()}</span>
          </div>
        </div>
        <div className='flex-grow-1'>
          <div className='d-flex justify-content-between align-items-start flex-wrap mb-2'>
            <div className='d-flex flex-column'>
              <div className='d-flex align-items-center mb-2'>
                <span className='text-gray-800 fs-1 fw-bolder me-1' data-testid='bmTitle'>
                  {messageData?.name}
                </span>
                {messageData?.pendingMessage &&
                (messageData?.canceledAt || messageData?.cancelledAt || messageData?.pausedAt) ? (
                  <span className={` m-3 badge badge-light-danger fw-bold`} data-testid='bmStatus'>
                    {messageData?.canceledAt || messageData?.cancelledAt
                      ? 'Canceled by user'
                      : 'Paused'}
                  </span>
                ) : (
                  <span
                    className={` m-3 badge badge-light-${
                      messageData?.finalStatus?.toLowerCase() == 'completed' ? 'success' : 'success'
                    } fw-bold`}
                    data-testid='bmStatus'
                  >
                    {messageData?.pendingMessage
                      ? 'In Progress'
                      : messageData?.finalStatus?.toUpperCase() || 'Completed'}
                  </span>
                )}
              </div>
              <div className='d-flex flex-wrap fw-bold fs-6 mb-1 pe-2'>
                <span
                  className='d-flex align-items-center text-hover-primary me-5 mb-2'
                  style={{color: '#767687'}}
                  data-testid='bmSentOn'
                >
                  {!isStagger && (
                    <>
                      <span className='me-1' style={{color: '#191C30'}}>
                        Sent on
                      </span>
                      {moment(messageData?.createdAt).format('HH:mm DD/MM/YYYY')}
                    </>
                  )}

                  {isStagger && (
                    <>
                      <span className='me-1' style={{color: '#191C30'}}>
                        Sent on
                      </span>
                      <br />
                      {staggerMessage
                        .map((el) => moment(el?.scheduleAtDate).format('HH:mm DD/MM/YYYY'))
                        .join(' , ')}
                    </>
                  )}
                </span>
              </div>
            </div>

            <div>
              {messageData?.pendingMessage > 0 && (
                <>
                  {renderActionButton({
                    show: messageData?.pausedAt,
                    tooltip: 'Resume',
                    icon: 'bi-arrow-clockwise',
                    colorClass: 'btn-info',
                    loading: resumeCampaignLoading,
                    loadingIdParam: resumeIdParam,
                    setIdParam: setResumeIdParam,
                    openConfirmation: setIsOpenResumeConfirmation,
                  })}

                  {/* Pause Button */}
                  {renderActionButton({
                    show: !messageData?.pausedAt,
                    tooltip: 'Pause Campaign',
                    icon: 'bi-pause',
                    colorClass: 'btn-warning',
                    loading: pauseCampaignLoading,
                    loadingIdParam: pauseIdParam,
                    setIdParam: setPauseIdParam,
                    openConfirmation: setIsOpenPauseConfirmation,
                  })}

                  {/* Cancel Button */}
                  {renderActionButton({
                    show: !messageData?.canceledAt,
                    tooltip: 'Cancel Campaign',
                    icon: 'bi-x',
                    colorClass: 'btn-danger',
                    loading: cancelCampaignLoading,
                    loadingIdParam: cancelIdParam,
                    setIdParam: setCancelIdParam,
                    openConfirmation: setIsOpenCancelConfirmation,
                  })}
                </>
              )}
            </div>
          </div>

          <div className='d-flex flex-wrap flex-stack account-stack'>
            <div className='d-flex flex-column flex-grow-1 pe-8'>
              <div className='d-flex flex-wrap'>
                <div className='border border-gray-300 border-solid rounded min-w-125px py-2 px-4 me-4 mb-3'>
                  <div className='d-flex align-items-center'>
                    <div className='fs-3 fw-bolder' data-testid='bmSendCost'>
                      ${+messageData?.totalCost?.toFixed(4)}
                    </div>
                  </div>
                  <div className='fw-bold fs-6 text-gray-400'>Send cost</div>
                </div>
                <div className='border border-gray-300 border-solid rounded min-w-125px py-2 px-4 me-4 mb-3'>
                  <div className='d-flex align-items-center'>
                    <div className='fs-3 fw-bolder' data-testid='bmDateSent'>
                      {isStagger
                        ? moment(latestSend?.scheduleAt).format('HH:mm DD/MM/YYYY')
                        : moment(messageData?.createdAt).format('HH:mm DD/MM/YYYY')}
                    </div>
                  </div>
                  <div className='fw-bold fs-6 text-gray-400'>
                    {isStagger ? 'Last sent' : 'Date sent'}
                  </div>
                </div>
                <div className='border border-gray-300 border-solid rounded min-w-125px py-2 px-4 me-4 mb-3'>
                  <div className='d-flex align-items-center'>
                    <div className='fs-3 fw-bolder' data-testid='bmSenderID'>
                      {messageData?.sender}
                    </div>
                  </div>
                  <div className='fw-bold fs-6 text-gray-400'>Sender ID</div>
                </div>
                <div
                  className='border border-gray-300 border-solid rounded min-w-125px py-2 px-4 me-4 mb-3'
                  data-testid='bmSentTo'
                >
                  <div className='d-flex align-items-center'>
                    <div className='fs-3 fw-bolder'>
                      {messageData?.to?.selectedType == 'segmentOrTags'
                        ? 'List, Segments & Tags'
                        : messageData?.to?.selectedType}{' '}
                      ({messageData?.recipients})
                    </div>
                  </div>
                  <div className='fw-bold fs-6 text-gray-400'>
                    To{' '}
                    {messageData?.to?.selectedType === 'segmentOrTags' &&
                      messageData?.targetNames &&
                      Array.isArray(messageData?.targetNames) &&
                      ' : ' + messageData?.targetNames.join(',')}
                  </div>
                </div>

                <div className='border border-gray-300 border-solid rounded min-w-125px py-2 px-4 me-4 mb-3'>
                  <div className='d-flex align-items-center'>
                    <div className='fs-3 fw-bolder' data-testid='bmCreditUsed'>
                      {isStagger ? staggerMessageAmount : +messageData?.creditAmount?.toFixed(4)}
                    </div>
                  </div>
                  <div className='fw-bold fs-6 text-gray-400'>Credit Used</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      {cancelConfirmation}
      {cancelSendingMessageConfirmation}
      {pauseSendingMessageConfirmation}
      {resumeSendingMessageConfirmation}
      <div className='card eofy_sale'>
        <div className='position-absolute m-3 cursor-pointer'>
          <KTSVG
            onClick={() => navigate('/report-analytics/bulk-messages')}
            path='/media/icons/duotune/arrows/arr022.svg'
            className='svg-icon-2x'
          />
        </div>
        <div className='card-body pt-9 pb-0 eofy_header'>
          {isDefaultRegion ? renderDefaultRegion() : renderRegionData()}
          <div className='d-flex overflow-auto h-55px'>
            <ul
              className='nav nav-stretch nav-tabs nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bolder flex-nowrap eofy_tabs'
              id='myTab'
              role='tablist'
            >
              <li className='nav-item' role='presentation'>
                <Link
                  className={
                    `nav-link text-active-primary me-6 ` +
                    (param?.pathname?.split('/')?.includes('snapshot') && 'active')
                  }
                  to={`/eofy/snapshot`}
                  data-testid='tabSnapshot'
                >
                  Snapshot
                </Link>
              </li>
              <li className='nav-item' role='presentation'>
                <Link
                  className={
                    `nav-link text-active-primary me-6 ` +
                    (param?.pathname?.split('/')?.includes('contacts') && 'active')
                  }
                  to={`/eofy/contacts`}
                  data-testid='tabContacts'
                >
                  Contacts
                </Link>
              </li>
              <li className='nav-item' role='presentation'>
                <Link
                  className={
                    `nav-link text-active-primary me-6 ` +
                    (param?.pathname?.split('/')?.includes('customer_replies') && 'active')
                  }
                  to={`/eofy/customer_replies`}
                  data-testid='tabCustomerReplies'
                >
                  Customer replies
                </Link>
              </li>
              {[
                messageData?.optout,
                messageData?.replyStopToOptOut,
                ReportId?.data?.is_reply_stop_out,
                ReportId?.data?.is_use_opt_out,
              ].includes(true) && (
                <li className='nav-item' role='presentation'>
                  <Link
                    className={
                      `nav-link text-active-primary me-6 ` +
                      (param?.pathname?.split('/')?.includes('opt_out') && 'active')
                    }
                    to={`/eofy/opt_out`}
                    data-testid='tabOptOut'
                  >
                    Opt-out
                  </Link>
                </li>
              )}
              <li className='nav-item' role='presentation'>
                <Link
                  className={
                    `nav-link text-active-primary me-6 ` +
                    (param?.pathname?.split('/')?.includes('short_link') && 'active')
                  }
                  to={`/eofy/short_link`}
                  data-testid='tabShortLink'
                >
                  Short link
                </Link>
              </li>
              <li className='nav-item' role='presentation'>
                <Link
                  className={
                    `nav-link text-active-primary me-6 ` +
                    (param?.pathname?.split('/')?.includes('bounced') && 'active')
                  }
                  to={`/eofy/bounced`}
                  data-testid='tabFailed'
                >
                  Failed
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className='tab-content'>
          <Outlet />
        </div>
      </div>
    </>
  )
}

export default ReportPageHeader
